import PropTypes from 'prop-types';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { approveStockTransfer } from '#/api/imsMgmt/receipts/stockTransfer/approve';
import { cancelStockTransfer } from '#/api/imsMgmt/receipts/stockTransfer/cancel';
import { fetchStockTransferDetail } from '#/api/imsMgmt/receipts/stockTransfer/fetchDetail';
import { rejectStockTransfer } from '#/api/imsMgmt/receipts/stockTransfer/reject';
import { submitStockTransfer } from '#/api/imsMgmt/receipts/stockTransfer/submit';
import SideMsg from '#/modules/commonComponents/SideMsg';
import DocumentSideMsgActions from '#/modules/imsMgmt/receipts/shared/DocumentSideMsgActions';
import { useDocumentSideMsgWorkflow } from '#/modules/imsMgmt/receipts/shared/useDocumentSideMsgWorkflow.jsx';
import {
  stockTransferSideMsgDetailBlock,
  stockTransferSideMsgStatusBlock,
} from '#/modules/imsMgmt/receipts/stockTransfer/data/stockTransferSideMsgConfig';
import {
  getStockTransferApiErrorMessage,
  isStockTransferVersionConflict,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/apiError';
import {
  canApproveStockTransfer,
  canCancelStockTransfer,
  canRejectStockTransfer,
  canSubmitStockTransfer,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferStatus';
import useStockTransferExcelDownload from '#/modules/imsMgmt/receipts/stockTransfer/hooks/useStockTransferExcelDownload';
import {
  stockTransferDetailQueryKey,
  stockTransferListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CONFIRM_COPY = {
  submit: {
    title: '提交轉倉單',
    message: '提交後將鎖定來源庫存並進入待核准，確定提交？',
    confirmText: '提交',
  },
  approve: {
    title: '核准轉倉單',
    message: '核准後將移動／拆分庫存，確定核准？',
    confirmText: '核准',
  },
  reject: {
    title: '退回轉倉單',
    message: '退回後將恢復為草稿並釋放鎖定，確定退回？',
    confirmText: '退回',
  },
  cancel: {
    title: '刪除草稿',
    message: '刪除後無法復原，確定刪除？',
    confirmText: '刪除',
  },
};

const APIS = {
  submit: submitStockTransfer,
  approve: approveStockTransfer,
  reject: rejectStockTransfer,
  cancel: cancelStockTransfer,
  fetchDetail: fetchStockTransferDetail,
};

const StockTransferSideMsg = ({ displayData, setSelectedData }) => {
  const canCreate = useCan(PERM.STOCK_TRANSFER_CREATE);
  const canReview = useCan(PERM.STOCK_TRANSFER_REVIEW);

  const workflow = useDocumentSideMsgWorkflow({
    row: displayData,
    setSelectedData,
    confirmCopy: CONFIRM_COPY,
    canSubmit: (status) => canCreate && canSubmitStockTransfer(status),
    canApprove: (status) => canReview && canApproveStockTransfer(status),
    canReject: (status) => canReview && canRejectStockTransfer(status),
    canCancel: (status) => canCreate && canCancelStockTransfer(status),
    apis: APIS,
    listQueryKeyPrefix: stockTransferListQueryKeyPrefix,
    detailQueryKey: stockTransferDetailQueryKey,
    getErrorMessage: getStockTransferApiErrorMessage,
    isVersionConflict: isStockTransferVersionConflict,
  });

  const excelDownload = useStockTransferExcelDownload(displayData?.id);
  const hasActions = (
    excelDownload.canPrint
    || workflow.canSubmit
    || workflow.canApprove
    || workflow.canReject
    || workflow.canCancel
  );

  const blocks = [
    stockTransferSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <DocumentSideMsgActions
                canPrint={excelDownload.canPrint}
                canSubmit={workflow.canSubmit}
                canApprove={workflow.canApprove}
                canReject={workflow.canReject}
                canCancel={workflow.canCancel}
                isPending={workflow.isPending}
                isPrintPending={excelDownload.isPrintPending}
                onPrint={excelDownload.handlePrint}
                onSubmit={workflow.handleSubmit}
                onApprove={workflow.handleApprove}
                onReject={workflow.handleReject}
                onCancel={workflow.handleCancel}
              />
            </div>
          ),
        },
      ],
    },
    stockTransferSideMsgDetailBlock,
  ];

  return (
    <>
      {workflow.ConfirmDialogHost}
      <workflow.RejectDialogHost />
      <SideMsg
        title="轉倉單"
        titleValue="transferNo"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

StockTransferSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default StockTransferSideMsg;
